import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSettings } from "../contexts/SettingsContext";

const TableComponent = ({
  data = [],
  columns = [],
  filters = [],
  searchPlaceholder = "Search...",
  showPagination = true,
  itemsPerPage: initialItemsPerPage = 10,
  onRowClick = null,
  expandedRowId = null,
  onRowToggle = null,
  renderExpandedRow = null,
  onActionClick = null,
  title = "Data Table",
  showFilter = true,
  disableHorizontalScroll = false,
  compactMode = false,
  showJobFilterDropdown = false,
  jobFilterOptions = [],
  onJobFilterChange = null,
  queryKey = "",
  initialCurrentPage = 1,
  onPageChange = null,
  // New: optional server-side mode (keeps old design/flow intact)
  serverSide = false,
  serverFetchData = null,
}) => {
  const { settings } = useSettings();
  const primaryColor = settings?.theme?.colors?.primary || "#12B886";
  const secondaryColor = settings?.theme?.colors?.secondary || "#20C997";

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState(filters[0]?.key || "all");
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [selectedJobFilter, setSelectedJobFilter] = useState("");

  // server-side state
  const [serverRows, setServerRows] = useState([]);
  const [serverTotalRecords, setServerTotalRecords] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [serverLoading, setServerLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  // cache of pages for current query signature
  const pageCacheRef = useRef({});
  const filterMenuRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const querySignature = useMemo(
    () =>
      JSON.stringify({
        activeFilter,
        debouncedSearchTerm,
        sortBy,
        sortOrder,
        itemsPerPage,
        columnFilters,
        selectedJobFilter,
        queryKey,
      }),
    [activeFilter, debouncedSearchTerm, sortBy, sortOrder, itemsPerPage, columnFilters, selectedJobFilter, queryKey]
  );

  useEffect(() => {
    if (!serverSide) return;
    pageCacheRef.current = {};
    setCurrentPage(1);
  }, [serverSide, querySignature]);

  useEffect(() => {
    setCurrentPage(initialCurrentPage);
  }, [initialCurrentPage]);

  useEffect(() => {
    if (!showAdvancedFilters || !showJobFilterDropdown) return;

    const onClickOutside = (event) => {
      if (!filterMenuRef.current) return;
      if (!filterMenuRef.current.contains(event.target)) {
        setShowAdvancedFilters(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [showAdvancedFilters, showJobFilterDropdown]);

  useEffect(() => {
    if (!serverSide || typeof serverFetchData !== "function") return;

    const cached = pageCacheRef.current[currentPage];

    // Use cache for any previously loaded page (prev/next/number click).
    if (cached) {
      setServerRows(cached.data || []);
      setServerTotalRecords(cached.total_records || 0);
      setServerTotalPages(cached.total_pages || 1);
      setServerError("");
      setServerLoading(false);
      return;
    }

    let cancelled = false;

    const fetchServerData = async () => {
      setServerLoading(true);
      setServerError("");
      try {
        const activeFilterObj = filters.find((f) => f.key === activeFilter);
        const payload = await serverFetchData({
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearchTerm,
          sort_by: sortBy,
          sort_order: sortOrder,
          job_filter: selectedJobFilter,
          statusTabFilter:
            showFilter && activeFilterObj && activeFilter !== "all"
              ? {
                  key: activeFilterObj.filterKey,
                  value: activeFilterObj.matchValue || activeFilter,
                }
              : null,
          filters: columnFilters,
        });

        if (cancelled) return;

        const normalized = {
          page: payload?.page || currentPage,
          limit: payload?.limit || itemsPerPage,
          total_records: payload?.total_records || 0,
          total_pages: payload?.total_pages || 1,
          data: Array.isArray(payload?.data) ? payload.data : [],
        };

        pageCacheRef.current[currentPage] = normalized;
        setServerRows(normalized.data);
        setServerTotalRecords(normalized.total_records);
        setServerTotalPages(normalized.total_pages);
      } catch (err) {
        if (cancelled) return;
        setServerRows([]);
        setServerTotalRecords(0);
        setServerTotalPages(1);
        setServerError(err?.message || "Failed to fetch data");
      } finally {
        if (!cancelled) setServerLoading(false);
      }
    };

    fetchServerData();

    return () => {
      cancelled = true;
    };
  }, [
    serverSide,
    serverFetchData,
    currentPage,
    itemsPerPage,
    debouncedSearchTerm,
    sortBy,
    sortOrder,
    selectedJobFilter,
    activeFilter,
    filters,
    showFilter,
    columnFilters,
  ]);

  // Filter data based on active filter (client mode)
  const filteredByStatus = useMemo(() => {
    if (serverSide) return data;
    if (!showFilter || !activeFilter || activeFilter === "all") return data;

    return data.filter((item) => {
      const activeFilterObj = filters.find((f) => f.key === activeFilter);
      if (!activeFilterObj || !activeFilterObj.filterKey) return true;

      const filterValue = item[activeFilterObj.filterKey];
      return activeFilterObj.matchValue
        ? String(filterValue || "").toLowerCase() === String(activeFilterObj.matchValue).toLowerCase()
        : String(filterValue || "").toLowerCase() === activeFilter.toLowerCase();
    });
  }, [data, activeFilter, filters, showFilter, serverSide]);

  // Search through filtered data (client mode)
  const searchedData = useMemo(() => {
    if (serverSide) return filteredByStatus;
    if (!searchTerm.trim()) return filteredByStatus;

    return filteredByStatus.filter((item) => {
      return columns.some((col) => {
        const value = item[col.key];
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
    });
  }, [filteredByStatus, searchTerm, columns, serverSide]);

  // Sorting (client mode)
  const sortedData = useMemo(() => {
    if (serverSide) return searchedData;
    if (!sortBy) return searchedData;

    const arr = [...searchedData];
    arr.sort((a, b) => {
      const av = a?.[sortBy];
      const bv = b?.[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      const an = Number(av);
      const bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        return sortOrder === "asc" ? an - bn : bn - an;
      }

      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return sortOrder === "asc" ? -1 : 1;
      if (as > bs) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [searchedData, sortBy, sortOrder, serverSide]);

  const totalPages = serverSide
    ? serverTotalPages
    : Math.ceil(sortedData.length / itemsPerPage) || 1;

  const paginatedData = useMemo(() => {
    if (serverSide) return serverRows;
    const startIdx = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIdx, startIdx + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage, serverSide, serverRows]);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
    if (typeof onPageChange === "function") onPageChange(1);
  };

  const handlePageChange = (page) => {
    const nextPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(nextPage);
    if (typeof onPageChange === "function") onPageChange(nextPage);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    if (typeof onPageChange === "function") onPageChange(1);
  };

  const handleFilterChange = (filterKey) => {
    setActiveFilter(filterKey);
    setCurrentPage(1);
    if (typeof onPageChange === "function") onPageChange(1);
  };

  const handleColumnSort = (columnKey) => {
    if (!columnKey) return;
    if (sortBy === columnKey) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnKey);
      setSortOrder("asc");
    }
    setCurrentPage(1);
    if (typeof onPageChange === "function") onPageChange(1);
  };

  const setAdvancedFilterValue = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    if (typeof onPageChange === "function") onPageChange(1);
  };

  const handleJobFilterChange = (e) => {
    const value = e.target.value || "";
    setSelectedJobFilter(value);
    if (typeof onJobFilterChange === "function") {
      onJobFilterChange(value);
    }
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (value) next.headline = value;
      else delete next.headline;
      return next;
    });
    setCurrentPage(1);
    if (typeof onPageChange === "function") onPageChange(1);
  };

  const resetAdvancedFilters = () => {
    setColumnFilters({});
    setCurrentPage(1);
  };

  // Render cell content based on column type, safely handling objects/arrays
  const renderCell = (item, column) => {
    const value = item[column.key];

    if (column.render) {
      return column.render(value, item);
    }

    // If value is an object or array, render as a JSON string (or join array of primitives)
    if (Array.isArray(value)) {
      // If array of primitives, join; if array of objects, JSON.stringify
      if (value.every(v => typeof v === 'string' || typeof v === 'number')) {
        return value.join(', ');
      }
      return JSON.stringify(value);
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    return value;
  };

  const totalEntries = serverSide ? serverTotalRecords : sortedData.length;

  return (
    <div className="w-full bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden transition-colors duration-250">
      {/* Header Section */}
      <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 sm:px-6 sm:py-4">
        <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between 2xl:gap-4">
          {/* Title */}
          <h2 className="text-base font-semibold text-gray-800 dark:text-white whitespace-nowrap sm:text-lg">
            {title}
          </h2>

          {/* Right side: Search Bar, Filters, and Filter Icon */}
          <div className="flex w-full flex-wrap items-center gap-3 2xl:w-auto 2xl:flex-nowrap 2xl:gap-4">
            {/* Filters */}
            {showFilter && filters.length > 0 && (
              <div className="flex w-full gap-2 overflow-x-auto pb-1 2xl:w-auto 2xl:overflow-visible 2xl:pb-0">
                {filters.map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => handleFilterChange(filter.key)}
                    className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all duration-200 font-medium sm:px-4 sm:py-2 sm:text-sm 
                      ${activeFilter === filter.key
                        ? 'text-black dark:text-white bg-[#12B88626] dark:bg-[#12B88655] border border-[#12B886] dark:border-[#12B886]'
                        : 'text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'}
                    `}
                    style={{}}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            )}

            {/* Search Bar */}
            <div className="relative flex min-w-[220px] flex-1 items-center md:min-w-[280px] 2xl:w-72 2xl:min-w-0 2xl:flex-none">
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full px-4 py-2.5 pl-4 pr-10 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-gray-300 dark:focus:border-gray-500 transition-colors duration-200"
              />
              <svg
                className="w-5 h-5 absolute right-3 text-gray-400 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Filter Icon Button */}
            <div className="relative flex-shrink-0" ref={filterMenuRef}>
              <button
                type="button"
                onClick={() => setShowAdvancedFilters((s) => !s)}
                className="p-2.5 rounded-lg transition-colors duration-200"
                style={{
                  border: "1px solid #12B886",
                  background: "#12B88626",
                  color: "#12B886",
                }}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
              </button>

              {showAdvancedFilters && showJobFilterDropdown && (
                <div className="absolute right-0 mt-2 z-30 min-w-[280px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg p-3">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Filter by Job Posting</div>
                  <select
                    value={selectedJobFilter}
                    onChange={handleJobFilterChange}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 text-sm"
                  >
                    <option value="">All Job Postings</option>
                    {jobFilterOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedJobFilter("");
                        if (typeof onJobFilterChange === "function") {
                          onJobFilterChange("");
                        }
                        setColumnFilters({});
                        setCurrentPage(1);
                        if (typeof onPageChange === "function") onPageChange(1);
                      }}
                      className="px-2.5 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {showAdvancedFilters && !showJobFilterDropdown && (
          <div className="mt-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              {columns.map((col) => (
                <input
                  key={`filter_${col.key}`}
                  type="text"
                  value={columnFilters[col.key] || ""}
                  onChange={(e) => setAdvancedFilterValue(col.key, e.target.value)}
                  placeholder={`Filter ${col.label}`}
                  className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm"
                />
              ))}
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={resetAdvancedFilters}
                className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className={disableHorizontalScroll ? "overflow-x-auto xl:overflow-x-visible" : "overflow-x-auto"}>
        <table className={disableHorizontalScroll ? "w-full min-w-[1120px] table-auto xl:min-w-0 xl:table-fixed" : "w-full min-w-[920px] sm:min-w-full"}>
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => column.sortable !== false && handleColumnSort(column.key)}
                  className={`${compactMode ? "px-3 py-2 text-xs" : "px-6 py-3 text-sm"} text-left font-semibold text-gray-700 dark:text-gray-300 ${
                    column.sortable !== false ? "cursor-pointer" : "cursor-default"
                  }`}
                  style={{
                    width: column.width || 'auto',
                    minWidth: column.minWidth || 'auto',
                    maxWidth: column.maxWidth || 'auto',
                  }}
                >
                  <span className="inline-flex items-center gap-1">
                    {column.label}
                    {column.sortable !== false && (
                      <span className="text-gray-400 text-xs">
                        {sortBy === column.key ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {serverError ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-8 text-center text-red-500"
                >
                  {serverError}
                </td>
              </tr>
            ) : (serverLoading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  <div className="flex items-center justify-center">
                    <div
                      className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 dark:border-gray-600 border-t-transparent"
                      style={{ borderTopColor: primaryColor }}
                    />
                  </div>
                </td>
              </tr>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((item, idx) => (
                <React.Fragment key={item.id || idx}>
                <tr
                  className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 cursor-pointer"
                  onClick={() => {
                    if (onRowToggle) {
                      onRowToggle(item);
                    } else if (onRowClick) {
                      onRowClick(item);
                    }
                  }}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`${compactMode ? "px-3 py-2 text-xs" : "px-6 py-4 text-sm"} text-gray-700 dark:text-gray-300 align-top break-words`}
                      style={{
                        width: column.width || 'auto',
                        minWidth: column.minWidth || 'auto',
                        maxWidth: column.maxWidth || 'auto',
                      }}
                    >
                      {renderCell(item, column)}
                    </td>
                  ))}
                </tr>
                {expandedRowId && (item.id || idx) === expandedRowId && renderExpandedRow && (
                  <tr>
                    <td colSpan={columns.length} className="p-0 border-b border-gray-200 dark:border-gray-700">
                      <div style={{ borderLeft: `3px solid ${primaryColor}` }} onClick={(e) => e.stopPropagation()}>
                        {renderExpandedRow(item)}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  No data found
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer with Pagination */}
      {showPagination && totalEntries > 0 && (
        <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2 sm:px-6 sm:py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center justify-between gap-3 md:justify-start md:gap-4">
            <label className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5 sm:text-sm sm:gap-2">
              Show
              <select
                className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none text-xs sm:text-sm"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              entries
            </label>
            <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto pb-1 sm:gap-2 sm:overflow-visible sm:pb-0">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-xs sm:px-3 sm:text-sm"
            >
              &lt;
            </button>
            {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => {
              let pageNum;
              const pages = totalPages || 1;
              if (pages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= pages - 2) {
                pageNum = pages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => {
                    handlePageChange(pageNum);
                  }}
                  className={`min-w-[30px] h-8 px-2.5 rounded-lg font-medium text-xs transition-all duration-200 sm:min-w-[32px] sm:px-3 sm:text-sm ${
                    currentPage === pageNum
                      ? "text-white"
                      : "border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                  style={
                    currentPage === pageNum
                      ? {
                          background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                          border: "none",
                        }
                      : {}
                  }
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => {
                handlePageChange(currentPage + 1);
              }}
              disabled={currentPage === (totalPages || 1)}
              className="px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-xs sm:px-3 sm:text-sm"
            >
              &gt;
            </button>
            </div>
          </div>
          <span className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalEntries)} to {Math.min(currentPage * itemsPerPage, totalEntries)} of {totalEntries} entries
          </span>
        </div>
      )}
    </div>
  );
};

export default TableComponent;
